import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import {
  dateCommencement,
  setHomecareLevel5Inpatient,
  setSelectedTariffs,
  setSicknessDailyAllowance,
  clearPersonResults,
} from '../../store/reducers/contribution.reducer';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  LocalizationProvider,
  DatePicker,
  PickersDay,
  PickersDayProps,
} from '@mui/x-date-pickers';
import {
  Grid,
  FormControl,
  TextField,
  FormHelperText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';

dayjs.locale('de');

export default function DefaultDateSelection() {
  const dispatch = useAppDispatch();
  const { mandant, isColorBlindMode } = useAppSelector((state) => state.config);
  const { commencementDate } = useAppSelector((state) => state.contribution);

  const [value, setValue] = useState<Dayjs | null>(null);

  const {
    personsId,
    homecareLevel5Inpatient,
    daySicknessDailyAllowance1,
    rateSicknessDailyAllowance1,
    selectedTariffs,
  } = useAppSelector((state) => state.contribution);
  const currentStep = useAppSelector((state) => state.stepper.steps);

  useEffect(() => {
    if (commencementDate) {
      setValue(dayjs(commencementDate));
    }
  }, [commencementDate]);

  const [modalOpen, setModalOpen] = useState<number>(0);

  // Constants
  const DAY_OPTIONS = [43, 64, 85, 92, 106, 127, 183, 274, 365];

  // Track whether we've already initialized from URL params
  const [hasInitialized, setHasInitialized] = useState(false);
  const [visibleTariffs, setVisibleTariffs] = useState<string[]>([]);
 
  // Derive checkbox states directly from Redux
  const krankenhausChecked = selectedTariffs.includes('Stationär');
  const zahnChecked = selectedTariffs.includes('Zahn');
  const pflegetagegeldParent = selectedTariffs.includes('Pflegetagegeld');
  const krankentagegeldParent = selectedTariffs.includes('Krankentagegeld');

  // Initialize from URL params only on VERY FIRST mount
  useEffect(() => {
    if (hasInitialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    const tariffTypes = urlParams.get('tarifftypes');

    if (tariffTypes && selectedTariffs.length === 0) {
      // Only set from URL if Redux is empty (truly first load)
      const tariffList = tariffTypes.split(',');
      setVisibleTariffs(tariffList);

      const normalizedTypes = tariffTypes.toLowerCase();
      const selected: string[] = [];

      if (
        normalizedTypes.includes('stationar') ||
        normalizedTypes.includes('stationär')
      ) {
        selected.push('Stationär');
      }
      if (normalizedTypes.includes('zahn')) {
        selected.push('Zahn');
      }
      if (
        normalizedTypes.includes('pflegetagegeld') ||
        normalizedTypes.includes('pflegebar')
      ) {
        selected.push('Pflegetagegeld');
      }
      if (normalizedTypes.includes('krankentagegeld')) {
        selected.push('Krankentagegeld');
      }

      dispatch(setSelectedTariffs(selected));
    }

    setHasInitialized(true);
  }, [hasInitialized, selectedTariffs, dispatch]);


  useEffect(() => {
    if (modalOpen === 2 ){
      setTimeout(() => setModalOpen(0), 3000);
    } else if (modalOpen > 0 ){
      setTimeout(() => setModalOpen(0), 10000);
    }  
  }, [modalOpen]);

  const handleDateChange = (newValue: Dayjs | null) => {
    let modalOpen = 0;
    if (newValue) {
      if (mandant === 'hallesche' && newValue.diff(newValue.startOf('month'))) {
        newValue = newValue.startOf('month');
        modalOpen = 2;
      }

      const selectedDate = newValue.format('YYYY-MM-DD');
      const minDateStr = minSelectableDate.format('YYYY-MM-DD');
      const maxDateStr = maxSelectableDate.format('YYYY-MM-DD');
      
      if (selectedDate !== "Invalid Date" && (selectedDate < minDateStr || selectedDate > maxDateStr)) {
        modalOpen = modalOpen + 1;
        personsId.forEach((id) => {
          dispatch(clearPersonResults(id));
        });
      }

      if (modalOpen > 0){
        setModalOpen(modalOpen);
      }
    }
    setValue(newValue);
    dispatch(dateCommencement(newValue?.format('YYYY-MM-DD').substring(0, 10)));
  };

  const handleError = (e: any) => {
      e !== null && dispatch(dateCommencement('Invalid Da'));
  };

  const renderCustomDayColorBlind = (
    day: Dayjs,
    _selectedDays: Array<Dayjs | null>,
    pickersDayProps: PickersDayProps<Dayjs>
  ) => {
    const isSelected = value ? day.isSame(value, 'day') : false;
    return (
      <PickersDay
        {...pickersDayProps}
        disableMargin
        sx={{
          fontSize: '13px',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 10,
            left: '35%',
            width: '30%',
            borderBottom: '1px solid black',
          },
          ...(isSelected && {
            backgroundColor: 'accessibility.button.main !important',
            color: 'accessibility.button.contrastText',
            '&:hover': { backgroundColor: 'accessibility.button.main' },
          }),
        }}
      />
    );
  };

  // Determine if a tariff should be visible
  const isVisible = (tariffName: string) => {
    return visibleTariffs.length === 0 || visibleTariffs.includes(tariffName);
  };

  if (mandant !== 'hallesche') return null;

  const currentDate = dayjs();
  let minSelectableDate = currentDate.startOf('month');
  let maxSelectableDate = currentDate.add(6, 'month').startOf('month');
  if (currentDate.date() > 15) {
    minSelectableDate = minSelectableDate.add(1, 'month');
  }

  return (
    <Grid container spacing={2} paddingTop={2} paddingBottom={2}>
      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={modalOpen > 0}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          { (modalOpen === 2 || modalOpen === 3 ) && "Der Starttermin ist nur am ersten Tag des Monats möglich."}
          { (modalOpen === 3 ) && <p/>}
          { (modalOpen === 1 || modalOpen === 3 ) && "Der gewählte Versicherungsbeginn ist nicht zulässig:"}
          { (modalOpen === 1 || modalOpen === 3 ) && <br/>}
          { (modalOpen === 1 || modalOpen === 3 ) && "Bis einschließlich zum 15. eines Monats kann der Versicherungsbeginn zum Monatsersten des aktuellen Monats gewählt werden, ab dem 16. eines Monats zum Monatsersten des Folgemonats. Zusätzlich sind maximal 5 weitere Monate in der Zukunft zulässig. Bitte prüfen Sie Ihre Eingabe."}
        </Alert>
      </Snackbar>

      {/* Date Picker */}
      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="deDE">
            <DatePicker
              onError={handleError}
              label="Beginn der Versicherung"
              inputFormat="DD.MM.YYYY"
              value={value}
              onChange={handleDateChange}
              disabled={currentStep === 2}
              renderDay={
                isColorBlindMode ? renderCustomDayColorBlind : undefined
              }
              renderInput={(params) => (
                <TextField variant="filled" {...params} helperText="" />
              )}
              shouldDisableDate={(date) => {
                const currentDate = dayjs();
                const currentDay = currentDate.date();
                const selectedDate = date ? dayjs(date) : null;

                // Disable all dates other than the 1st of each month
                if (selectedDate && selectedDate.date() !== 1) {
                  return true;
                }

                // Disable the 1st of the current month if the current day is between the 16th and end of month
                if (
                  selectedDate &&
                  currentDay >= 16 &&
                  currentDay <= currentDate.endOf('month').date() &&
                  selectedDate.month() === currentDate.month()
                ) {
                  return true;
                }

                // Enable the 1st of the current month if the current day is between the 1st and 15th
                if (
                  selectedDate &&
                  currentDay >= 1 &&
                  currentDay <= 15 &&
                  selectedDate.month() === currentDate.month()
                ) {
                  return false;
                }

                // Disable dates beyond 6 months in the future
                if (selectedDate && selectedDate.isAfter(maxSelectableDate)) {
                  return true;
                }

                return false;
              }}
              minDate={dayjs(minSelectableDate.toDate())}
              maxDate={dayjs(maxSelectableDate.toDate())}
            />
          </LocalizationProvider>
          <FormHelperText sx={{ color: 'text.secondary', mt: 0.5 }}>
            TT.MM.JJJJ
          </FormHelperText>
        </FormControl>
      </Grid>

      { currentStep !== 2 && (
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Tarifvorgaben
        </Typography>
      </Grid>
      )}

      { currentStep !== 2 && (
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} sx={{ ml: 1 }}>
            <Grid container direction="column" spacing={1}>
              {/* Krankenhaus */}
              <Box sx={{ mt: 1 }}>
                <Tooltip
                  title={
                    <>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Krankenhaus
                      </div>
                      <div>
                        Aktivieren Sie diese Option, um
                        <br />
                        Krankenhauszusatzversicherungstarife in der
                        <br />
                        Analyse zu berücksichtigen.
                      </div>
                    </>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(97, 97, 97, 0.95)',
                        color: 'white',
                        fontSize: '0.875rem',
                        maxWidth: 300,
                        padding: '8px 12px',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={krankenhausChecked}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedTariffs, 'Stationär']
                            : selectedTariffs.filter((t) => t !== 'Stationär');
                          dispatch(setSelectedTariffs(newSelected));
                          // Clear results on any change
                          personsId.forEach((id) => {
                            dispatch(clearPersonResults(id));
                          });
                        }}
                      />
                    }
                    label={
                      <span style={{ fontWeight: 'bold' }}>Krankenhaus</span>
                    }
                  />
                </Tooltip>
              </Box>

              {/* Zahn */}
              <Box sx={{ mt: 1 }}>
                <Tooltip
                  title={
                    <>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Zahntarife
                      </div>
                      <div>
                        Aktivieren Sie diese Option, um Zahntarife in der
                        <br />
                        Analyse zu berücksichtigen.
                      </div>
                    </>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(97, 97, 97, 0.95)',
                        color: 'white',
                        fontSize: '0.875rem',
                        maxWidth: 300,
                        padding: '8px 12px',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={zahnChecked}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedTariffs, 'Zahn']
                            : selectedTariffs.filter((t) => t !== 'Zahn');
                          dispatch(setSelectedTariffs(newSelected));
                          // Clear results on any change
                          personsId.forEach((id) => {
                            dispatch(clearPersonResults(id));
                          });
                        }}
                      />
                    }
                    label={<span style={{ fontWeight: 'bold' }}>Zahn</span>}
                  />
                </Tooltip>
              </Box>

              {/* Pflegetagegeld/Pflegebar */}
              <Box sx={{ mt: 1 }}>
                <Tooltip
                  title={
                    <>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Pflegezusatz
                      </div>
                      <div>
                        Aktivieren Sie diese Option, um
                        <br />
                        Pflegezusatztarife zu berücksichtigen.
                      </div>
                    </>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(97, 97, 97, 0.95)',
                        color: 'white',
                        fontSize: '0.875rem',
                        maxWidth: 300,
                        padding: '8px 12px',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pflegetagegeldParent}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedTariffs, 'Pflegetagegeld']
                            : selectedTariffs.filter(
                                (t) => t !== 'Pflegetagegeld'
                              );
                          dispatch(setSelectedTariffs(newSelected));
                          // Clear results on any change
                          personsId.forEach((id) => {
                            dispatch(clearPersonResults(id));
                          });
                        }}
                      />
                    }
                    label="Pflegezusatz"
                    componentsProps={{
                      typography: { fontWeight: 'bold' },
                    }}
                  />
                </Tooltip>

                {pflegetagegeldParent && (
                  <Tooltip
                    title={
                      <>
                        <div
                          style={{ fontWeight: 'bold', marginBottom: '4px' }}
                        >
                          Gewünschte Absicherung / Monat
                        </div>
                        <div>
                          Bitte geben Sie hier ein, wie hoch das Monatsgeld mindestens sein soll.
                          Mindestbetrag: 100€, Maximalbetrag: 5000€
                        </div>
                      </>
                    }
                    arrow
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'rgba(97, 97, 97, 0.95)',
                          color: 'white',
                          fontSize: '0.875rem',
                          maxWidth: 300,
                          padding: '8px 12px',
                          '& .MuiTooltip-arrow': {
                            color: 'rgba(97, 97, 97, 0.95)',
                          },
                        },
                      },
                    }}
                  >
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Gewünschte Absicherung / Monat"
                        value={homecareLevel5Inpatient}
                        onChange={(e) => {
                          let val = Number(e.target.value);
                          if (val < 100) val = 100;
                          if (val > 5000) val = 5000;
                          dispatch(setHomecareLevel5Inpatient(val));
                          // Clear person results to require recalculation
                          personsId.forEach((id) => {
                            dispatch(clearPersonResults(id));
                          });
                        }}
                        inputProps={{ min: 100, max: 5000 }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {/* Krankentagegeld */}
              <Box sx={{ mt: 1 }}>
                <Tooltip
                  title={
                    <>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        Krankentagegeld
                      </div>
                      <div>
                        Aktivieren Sie diese Option, um
                        <br />
                        Krankentagegeldtarife zu berücksichtigen.
                      </div>
                    </>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(97, 97, 97, 0.95)',
                        color: 'white',
                        fontSize: '0.875rem',
                        maxWidth: 300,
                        padding: '8px 12px',
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(97, 97, 97, 0.95)',
                        },
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={krankentagegeldParent}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...selectedTariffs, 'Krankentagegeld']
                            : selectedTariffs.filter(
                                (t) => t !== 'Krankentagegeld'
                              );
                          dispatch(setSelectedTariffs(newSelected));
                          // Clear results on any change
                          personsId.forEach((id) => {
                            dispatch(clearPersonResults(id));
                          });
                        }}
                      />
                    }
                    label="Krankentagegeld"
                    componentsProps={{
                      typography: { fontWeight: 'bold' },
                    }}
                  />
                </Tooltip>

                {krankentagegeldParent && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Tooltip
                      title={
                        <>
                          <div
                            style={{ fontWeight: 'bold', marginBottom: '4px' }}
                          >
                            Krankentagegeld in Höhe von ... €
                          </div>
                          <div>
                            Geben Sie hier in € ein, in welcher Höhe das
                            1.Krankentagegeld berechnet werden soll. Das
                            Krankentagegeld über 30 € ist nicht
                            online-abschlussfähig!
                          </div>
                        </>
                      }
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(97, 97, 97, 0.95)',
                            color: 'white',
                            fontSize: '0.875rem',
                            maxWidth: 300,
                            padding: '8px 12px',
                            '& .MuiTooltip-arrow': {
                              color: 'rgba(97, 97, 97, 0.95)',
                            },
                          },
                        },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Krankentagegeld in Höhe von (€)"
                          value={rateSicknessDailyAllowance1}
                          onChange={(e) => {
                            let val = Number(e.target.value);
                            if (val < 1) val = 1;
                            if (val > 30) val = 30;
                            dispatch(
                              setSicknessDailyAllowance({
                                day: daySicknessDailyAllowance1,
                                rate: val,
                              })
                            );
                            // Clear person results to require recalculation
                            personsId.forEach((id) => {
                              dispatch(clearPersonResults(id));
                            });
                          }}
                          inputProps={{ min: 1, max: 30 }}
                        />
                      </Box>
                    </Tooltip>

                    <Tooltip
                      title={
                        <>
                          <div
                            style={{ fontWeight: 'bold', marginBottom: '4px' }}
                          >
                            Ab dem Tag
                          </div>
                          <div>
                            Geben Sie hier ein, ab welchem Tag das
                            <br />
                            1.Krankentagegeld berechnet werden soll.
                          </div>
                        </>
                      }
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(97, 97, 97, 0.95)',
                            color: 'white',
                            fontSize: '0.875rem',
                            maxWidth: 300,
                            padding: '8px 12px',
                            '& .MuiTooltip-arrow': {
                              color: 'rgba(97, 97, 97, 0.95)',
                            },
                          },
                        },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          select
                          fullWidth
                          label="Ab dem Tag"
                          value={daySicknessDailyAllowance1}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            dispatch(
                              setSicknessDailyAllowance({
                                day: val,
                                rate: rateSicknessDailyAllowance1,
                              })
                            );
                            // Clear person results to require recalculation
                            personsId.forEach((id) => {
                              dispatch(clearPersonResults(id));
                            });
                          }}
                        >
                          {DAY_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      )}

    </Grid>
  );
}
